import { syncKey } from "@repo/api";
import type {
  CorrectProcedureTrialInput,
  RecordProcedureTrialsInput,
  RetractProcedureTrialInput,
} from "@repo/api/schemas";
import type { Collection } from "@tanstack/db";
import { defineWrite, type WriteOptions } from "../actions/define-write";
import type { ObservationRow, ProcedureRow } from "../fhir/types";
import {
  type KeyedProcedure,
  type ProcedureIdentity,
  procedureFor,
  retractTrialReadings,
  type TrialRowKind,
  trialRowFor,
  writeTrialRow,
} from "./_procedure-optimistic";

type Rows<T extends ProcedureRow | ObservationRow> = Pick<Collection<T, string>, "state" | "insert" | "update">;

/**
 * What the optimistic write needs per kind: the stored trial row's vocabulary, and how a first trial begins the
 * procedure row. The server reads the kind off the order itself.
 */
export interface ProcedureTrialKind {
  row: TrialRowKind;
  begin: (input: ProcedureIdentity & { procedureId?: string }) => KeyedProcedure;
}

export interface ProcedureTrialDependencies {
  procedures: () => Rows<ProcedureRow>;
  observations: () => Rows<ObservationRow>;
  recordTrials: (input: RecordProcedureTrialsInput, options: WriteOptions) => Promise<unknown>;
  correctTrial: (input: CorrectProcedureTrialInput, options: WriteOptions) => Promise<unknown>;
  retractTrial: (input: RetractProcedureTrialInput, options: WriteOptions) => Promise<unknown>;
}

export function createProcedureTrialActions(deps: ProcedureTrialDependencies, kind: ProcedureTrialKind) {
  const recordTrials = defineWrite<RecordProcedureTrialsInput>({
    optimistic: (input) => {
      const procedure = kind.begin(input);
      for (const trial of input.trials) writeTrialRow(deps.observations(), procedure, kind.row, trial, false);
    },
    persist: (input, options) => deps.recordTrials(input, options),
  });
  const correctTrial = defineWrite<CorrectProcedureTrialInput>({
    coalesce: true,
    optimistic: (input) => {
      const procedure = procedureFor(deps.procedures(), input);
      if (!procedure || !trialRowFor(deps.observations(), procedure, kind.row, input.id))
        throw new Error(`Trial '${input.id}' is not loaded`);
      writeTrialRow(deps.observations(), procedure, kind.row, input, true);
    },
    persist: (input, options) => deps.correctTrial(input, options),
  });
  const retractTrial = defineWrite<RetractProcedureTrialInput>({
    optimistic: (input) => {
      const procedure = procedureFor(deps.procedures(), input);
      const existing = procedure && trialRowFor(deps.observations(), procedure, kind.row, input.id);
      if (!procedure || !existing) throw new Error(`Trial '${input.id}' is not loaded`);
      deps.observations().update(syncKey(existing), (draft) => {
        draft.status = "entered-in-error";
      });
      retractTrialReadings(deps.observations(), procedure.domainId, kind.row.idSystem, input.id);
    },
    persist: (input, options) => deps.retractTrial(input, options),
  });
  return { recordTrials, correctTrial, retractTrial };
}

export type ProcedureTrialActions = ReturnType<typeof createProcedureTrialActions>;
